import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Leaf, Filter as FilterIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'press', label: 'French Presses' },
  { id: 'infuser', label: 'Infusers & Strainers' },
  { id: 'kettle', label: 'Kettles' },
  { id: 'mug', label: 'Mugs & Cups' },
  { id: 'general', label: 'Other' },
];

export default function Accessories() {
  const [accessories, setAccessories] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAccessories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/accessories`);
        if (res.ok) {
          const data = await res.json();
          setAccessories(data.filter(a => a.inStock !== false));
        }
      } catch (err) {
        console.error('Error fetching accessories:', err);
      }
      setLoading(false);
    };
    fetchAccessories();
  }, []);

  const filtered = activeFilter === 'all'
    ? accessories
    : accessories.filter(a => a.category === activeFilter);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#faf6ee' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-md shadow-sm"
        style={{ backgroundColor: 'rgba(245, 233, 208, 0.95)' }}
      >
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_leaf-lounge-3/artifacts/kbshdl1e_file_00000000646471fdbb03789bab2f6e30.png"
              alt="Mudbutt Brew"
              className="h-14 w-auto"
            />
          </Link>
          <Link to="/">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              style={{ borderColor: '#8b5c2f', color: '#5e3a1c' }}
              data-testid="back-to-teas-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Teas
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 text-center" style={{ backgroundColor: '#5e3a1c' }}>
        <div className="container mx-auto px-6">
          <h1
            className="text-4xl md:text-5xl font-bold mb-3"
            style={{ color: '#c2a357', fontFamily: 'Georgia, serif' }}
            data-testid="accessories-title"
          >
            Tea Accessories & Equipment
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#f5e9d0' }}>
            Everything you need to brew the perfect cup
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <FilterIcon className="w-5 h-5 self-center mr-2" style={{ color: '#8b5c2f' }} />
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveFilter(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeFilter === cat.id
                  ? 'shadow-md transform scale-105'
                  : 'hover:shadow-sm'
              }`}
              style={{
                backgroundColor: activeFilter === cat.id ? '#8b5c2f' : '#f5e9d0',
                color: activeFilter === cat.id ? '#f5e9d0' : '#5e3a1c',
                border: '1px solid #c2a357',
              }}
              data-testid={`acc-filter-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="animate-pulse text-lg" style={{ color: '#8b5c2f' }}>Loading accessories...</div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-20" data-testid="accessories-empty">
            <Leaf className="w-20 h-20 mx-auto mb-6" style={{ color: '#c2a357' }} />
            <h2 className="text-2xl font-bold mb-3" style={{ color: '#5e3a1c', fontFamily: 'Georgia, serif' }}>
              Coming Soon
            </h2>
            <p className="text-lg mb-2 max-w-md mx-auto" style={{ color: '#8b5c2f' }}>
              We're curating the best tea-making gear for you.
            </p>
            <p className="text-sm mb-8" style={{ color: '#8b5c2f' }}>
              French presses, infusers, kettles, mugs, and more - all hand-picked to complement your Mudbutt Brew experience.
            </p>
            <Link to="/">
              <Button style={{ backgroundColor: '#8b5c2f', color: '#f5e9d0' }}>
                Browse Our Teas
              </Button>
            </Link>
          </div>
        )}

        {/* Product Grid */}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((item) => (
              <Card
                key={item.id}
                className="group hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 hover:-translate-y-1"
                style={{ borderColor: '#c2a357' }}
                data-testid={`accessory-card-${item.id}`}
              >
                {item.image ? (
                  <div className="relative h-64 overflow-hidden bg-white flex items-center justify-center">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-full w-auto object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                    {item.featured && (
                      <div
                        className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                        style={{ backgroundColor: '#c2a357', color: '#ffffff' }}
                      >
                        Featured
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="relative h-64 overflow-hidden flex items-center justify-center"
                    style={{ backgroundColor: '#f5e9d0' }}
                  >
                    <Leaf className="w-16 h-16" style={{ color: '#c2a357' }} />
                    {item.featured && (
                      <div
                        className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                        style={{ backgroundColor: '#c2a357', color: '#ffffff' }}
                      >
                        Featured
                      </div>
                    )}
                  </div>
                )}

                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle style={{ color: '#5e3a1c' }}>{item.name}</CardTitle>
                    <Badge style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}>
                      {CATEGORIES.find(c => c.id === item.category)?.label || item.category}
                    </Badge>
                  </div>
                  <p className="text-sm mt-1" style={{ color: '#8b5c2f' }}>{item.description}</p>
                </CardHeader>

                <CardContent>
                  <p className="text-2xl font-bold" style={{ color: '#c2a357' }}>
                    {item.price}
                  </p>
                </CardContent>

                <CardFooter>
                  <Link to="/?inquire=accessory" className="w-full">
                    <Button
                      className="w-full"
                      style={{ backgroundColor: '#8b5c2f', color: '#f5e9d0' }}
                      data-testid={`accessory-inquire-${item.id}`}
                    >
                      Inquire Now
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="py-8 text-center" style={{ backgroundColor: '#5e3a1c' }}>
        <p className="text-sm" style={{ color: '#c2a357' }}>
          &copy; {new Date().getFullYear()} Mudbutt Brew - Elma, Washington
        </p>
      </footer>
    </div>
  );
}
